<!--
 * @Description: 图片预览独立页面
 * @Author: yangyitao
 * @Date: 2026-05-14
-->
<template>
  <div class="image-preview-page" @click="handleClose" @wheel.prevent="handleWheel">
    <div v-if="loading" class="loading-tip">加载中...</div>
    <div
      v-else-if="imageUrl"
      class="image-container"
      @click.stop
    >
      <img
        :src="imageUrl"
        class="preview-image"
        :style="imageStyle"
        alt="预览图片"
        draggable="false"
      />
    </div>
    <div v-else class="empty-tip">{{ errorMsg }}</div>
    <!-- 底部工具栏 -->
    <div v-if="imageUrl && !loading" class="bottom-toolbar" @click.stop>
      <!-- 缩放组 -->
      <button class="tool-btn" title="放大" @click="zoomIn">+</button>
      <span class="tool-text">{{ Math.round(scale * 100) }}%</span>
      <button class="tool-btn" title="缩小" @click="zoomOut">-</button>
      <span class="tool-divider"></span>
      <!-- 旋转组 -->
      <button class="tool-btn" title="向左旋转90°" @click="rotateLeft">↺</button>
      <button class="tool-btn" title="向右旋转90°" @click="rotateRight">↻</button>
      <span class="tool-divider"></span>
      <!-- 翻转组 -->
      <button class="tool-btn" :class="{ active: flipX }" title="水平翻转" @click="toggleFlipX">↔</button>
      <button class="tool-btn" :class="{ active: flipY }" title="垂直翻转" @click="toggleFlipY">↕</button>
      <span class="tool-divider"></span>
      <!-- 重置 -->
      <button class="tool-btn tool-reset" title="全部重置" @click="zoomReset">重置</button>
    </div>
  </div>
</template>

<script>
import CryptoJS from "crypto-js";
import { downFile } from "@/api/uploadCenter";

const AES_KEY = CryptoJS.enc.Utf8.parse("Q0Vny98mvpJz8skZ");
const AES_IV = CryptoJS.enc.Utf8.parse("nKd9hmVJWHbJqECg");

export default {
  name: "ImagePreview",
  data() {
    return {
      imageUrl: "",
      loading: false,
      errorMsg: "",
      // 缩放相关
      scale: 1,
      minScale: 0.1,
      maxScale: 10,
      zoomStep: 0.2,
      // 旋转与翻转
      rotate: 0,
      flipX: false,
      flipY: false
    };
  },
  computed: {
    imageStyle() {
      const scaleX = this.flipX ? -1 : 1;
      const scaleY = this.flipY ? -1 : 1;
      return {
        transform: `scale(${this.scale}) rotate(${this.rotate}deg) scaleX(${scaleX}) scaleY(${scaleY})`,
        transition: "transform 0.2s ease"
      };
    }
  },
  created() {
    this.loadByFileId();
  },
  watch: {
    // 监听 fileId 参数变化，修改 URL 参数后回车时重新加载
    "$route.query.fileId": function(newFileId) {
      this.loadByFileId(newFileId);
    }
  },
  methods: {
    handleClose() {
      window.close();
    },
    // 滚轮缩放
    handleWheel(e) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -this.zoomStep : this.zoomStep;
      const newScale = this.scale + delta;
      if (newScale < this.minScale || newScale > this.maxScale) return;
      this.scale = newScale;
    },
    zoomIn() {
      this.setScale(this.scale + this.zoomStep);
    },
    zoomOut() {
      this.setScale(this.scale - this.zoomStep);
    },
    zoomReset() {
      this.scale = 1;
      this.rotate = 0;
    },
    setScale(newScale) {
      if (newScale < this.minScale || newScale > this.maxScale) return;
      this.scale = newScale;
    },
    // 旋转（以 90° 为步长循环，保持 translate 不变）
    rotateLeft() {
      this.rotate = (this.rotate - 90) % 360;
    },
    rotateRight() {
      this.rotate = (this.rotate + 90) % 360;
    },
    // 水平翻转
    toggleFlipX() {
      this.flipX = !this.flipX;
    },
    // 垂直翻转
    toggleFlipY() {
      this.flipY = !this.flipY;
    },
    // AES 解密
    // 后端使用 AES/CBC/PKCS5Padding + Base64.getUrlEncoder().withoutPadding()
    // 前端需将 URL-safe Base64（无填充）转为标准 Base64（有填充）后再解密
    decryptFileId(encryptedId) {
      let standardBase64 = encryptedId.replace(/_/g, "/").replace(/-/g, "+");
      // 补全 Base64 padding（后端 withoutPadding 去掉了 =）
      while (standardBase64.length % 4 !== 0) {
        standardBase64 += "=";
      }
      try {
        const bytes = CryptoJS.AES.decrypt(standardBase64, AES_KEY, {
          iv: AES_IV,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        });
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (!decrypted) {
          console.error("AES 解密结果为空");
          return "";
        }
        return decrypted;
      } catch (e) {
        console.error("AES 解密失败", e);
        return "";
      }
    },
    // 从路由参数读取 fileId 并加载图片
    loadByFileId(fileId) {
      const encryptedId = fileId || this.$route.query.fileId;
      if (!encryptedId) {
        this.imageUrl = "";
        this.errorMsg = "缺少 fileId 参数";
        return;
      }
      const id = this.decryptFileId(encryptedId);
      if (!id) {
        this.imageUrl = "";
        this.errorMsg = "fileId 解密失败";
        return;
      }
      this.fetchFileUrl(id);
    },
    // 根据 fileId 获取文件访问地址
    fetchFileUrl(fileId) {
      this.loading = true;
      downFile({ id: fileId })
        .then(res => {
          if (res.code == "OK" && res.data) {
            // 接口返回的 data 即文件 URL
            this.imageUrl = typeof res.data === "string" ? res.data : res.data.fileUrl || "";
          } else {
            this.errorMsg = res.message || "获取文件地址失败";
          }
        })
        .catch(() => {
          this.errorMsg = "获取文件地址失败";
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.image-preview-page {
  width: 100vw;
  height: 100vh;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  position: relative;
}

.image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.bottom-toolbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border-radius: 8px;
  padding: 8px 16px;
  z-index: 100;
}

.tool-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }

  &.active {
    background: rgba(64, 158, 255, 0.5);
  }
}

.tool-reset {
  width: auto;
  padding: 0 12px;
  font-size: 13px;
  margin-left: 4px;
}

.tool-text {
  color: #fff;
  font-size: 13px;
  min-width: 48px;
  text-align: center;
  user-select: none;
}

.tool-divider {
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.25);
  margin: 0 2px;
}

.empty-tip,
.loading-tip {
  color: #fff;
  font-size: 16px;
}
</style>
